import { useRef } from 'react';
import { ExportFileType } from '@common/ts/enums';
import { FileTypeToggleProps } from '@/types';

/**
 * Segmented control for the export file type (TELV1-485). Replaces three 16px
 * radios whose tiny targets caused missed clicks (TELV1-484): each option is
 * now a full-height button. Implements the WAI-ARIA radio-group pattern
 * (roving tabindex + arrow keys) so keyboard behaviour matches the radios it
 * replaces.
 */
function ExportFileTypeToggle({ fileType, setFileType }: FileTypeToggleProps) {
  // fileTypes are ['xlsx', 'csv', 'txt'] — xlsx first (default).
  const fileTypes = Object.values(ExportFileType);
  const groupRef = useRef<HTMLDivElement>(null);

  const onKeyDown = (event: React.KeyboardEvent) => {
    const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
    const backward = event.key === 'ArrowLeft' || event.key === 'ArrowUp';
    if (!forward && !backward) return;
    event.preventDefault();
    const currentIndex = fileTypes.indexOf(fileType);
    const nextIndex = (currentIndex + (forward ? 1 : -1) + fileTypes.length) % fileTypes.length;
    const nextType = fileTypes[nextIndex];
    setFileType(nextType);
    groupRef.current?.querySelector<HTMLButtonElement>(`#filetype-${nextType}`)?.focus();
  };

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      aria-labelledby="export-filetype-label"
      className="inline-flex h-10 w-fit overflow-hidden rounded-md border border-neutral-200 bg-white shadow-sm"
    >
      {fileTypes.map((type, index) => {
        const isSelected = fileType === type;
        return (
          <button
            key={type}
            type="button"
            role="radio"
            aria-checked={isSelected}
            id={`filetype-${type}`}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => setFileType(type)}
            onKeyDown={onKeyDown}
            className={`px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${
              index > 0 ? 'border-l border-neutral-200' : ''
            } ${isSelected ? 'bg-primary text-white' : 'text-dark-text hover:bg-neutral-100'}`}
          >
            .{type}
          </button>
        );
      })}
    </div>
  );
}

export default ExportFileTypeToggle;
